import React, { useEffect, useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { ExportButton } from '../components/ExportButton';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { PhoneInput } from '../components/PhoneInput';
import { ESTADOS } from '../components/StateCitySelect';
import cidadesBrasil from '../data/cidades-brasil.json';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  UserCircle,
  Filter,
  Search,
  TrendingUp,
  Users,
  Phone,
  Trash2,
  Pencil,
  Upload,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { toast } from 'sonner';
import { SupportersHeatmap } from '../components/SupportersHeatmap';
import { Combobox } from '../components/Combobox';
import { BulkImportWizard } from '../components/BulkImportWizard';
import { RotateCcw, Filter as FilterIcon } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '../components/ui/pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZES = [25, 50, 100, 200];

export const ApoiadoresPage = () => {
  // Page meta + KPIs (refresh on tenant)
  const [stats, setStats] = useState({ total: 0, identificados: 0, anonimos: 0 });
  const [evolution, setEvolution] = useState(null);
  const [heatmap, setHeatmap] = useState({ points: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [periodDays, setPeriodDays] = useState(30);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [primaryState, setPrimaryState] = useState('DF');

  // Filter options (loaded once)
  const [filterOpts, setFilterOpts] = useState({ estados: [], cidades: [], liderancas: [] });
  const [dobradas, setDobradas] = useState([]);
  const [leadersFull, setLeadersFull] = useState([]);

  // Pending filters (form state, not yet applied)
  const [pendingFilters, setPendingFilters] = useState({
    search: '',
    estado: '',
    cidade: '',
    lideranca_id: '',
    tipo_registro: 'identificado',
  });
  // Applied filters (used in queries)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    estado: '',
    cidade: '',
    lideranca_id: '',
    tipo_registro: 'identificado',
  });

  // Paginated rows
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);

  useEffect(() => {
    fetchInitial();
  }, []);

  useEffect(() => {
    fetchEvolution();
  }, [periodDays]);

  useEffect(() => {
    fetchRows();
  }, [page, pageSize, appliedFilters]);

  const fetchInitial = async () => {
    try {
      const [optsRes, heatRes, leadersRes, dobradasRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/supporters/filter-options`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/supporters-heatmap`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leaders`, { withCredentials: true }),
        // Dobradas may 403 for some roles; ignore in that case
        axios.get(`${BACKEND_URL}/api/dobradas`, { withCredentials: true })
          .catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setFilterOpts(optsRes.data);
      setHeatmap(heatRes.data || { points: [], total: 0 });
      setLeadersFull(leadersRes.data || []);
      setDobradas(dobradasRes.data || []);
      const allowed = settingsRes.data?.allowed_states || [];
      if (allowed.length > 0) setPrimaryState(allowed[0]);
      // KPIs from opts (rough but accurate)
      const totalIdent = optsRes.data.liderancas.reduce((acc, l) => acc + l.count, 0);
      setStats((s) => ({ ...s, identificados: totalIdent }));
    } catch (err) {
      toast.error('Erro ao carregar opções');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvolution = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/stats/supporters-evolution?days=${periodDays}`,
        { withCredentials: true }
      );
      setEvolution(data);
    } catch (err) {
      /* silent */
    }
  };

  const fetchRows = async () => {
    setRowsLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (appliedFilters.search) params.search = appliedFilters.search;
      if (appliedFilters.estado) params.estado = appliedFilters.estado;
      if (appliedFilters.cidade) params.cidade = appliedFilters.cidade;
      if (appliedFilters.lideranca_id) params.lideranca_id = appliedFilters.lideranca_id;
      if (appliedFilters.tipo_registro) params.tipo_registro = appliedFilters.tipo_registro;
      const { data } = await axios.get(`${BACKEND_URL}/api/supporters/paginated`, {
        params,
        withCredentials: true,
      });
      setRows(data.items || []);
      setPageTotal(data.total || 0);
      setPageCount(data.pages || 0);
      setStats((s) => ({ ...s, total: data.total }));
    } catch (err) {
      toast.error('Erro ao carregar apoiadores');
    } finally {
      setRowsLoading(false);
    }
  };

  // Cidade options depend on current estado selection (pending)
  const cidadeOptions = useMemo(() => {
    const list = pendingFilters.estado
      ? filterOpts.cidades.filter((c) => c.estado === pendingFilters.estado)
      : filterOpts.cidades;
    const seen = new Set();
    const out = [];
    list.forEach((c) => {
      if (!c.cidade || seen.has(c.cidade)) return;
      seen.add(c.cidade);
      out.push({ value: c.cidade, label: c.cidade, hint: `${c.count} apoiador(es)` });
    });
    return out.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [filterOpts.cidades, pendingFilters.estado]);

  const estadoOptions = useMemo(
    () =>
      filterOpts.estados
        .map((e) => ({ value: e.sigla, label: e.sigla, hint: `${e.count} apoiador(es)` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [filterOpts.estados]
  );

  const liderancaOptions = useMemo(
    () =>
      filterOpts.liderancas.map((l) => ({
        value: l.id,
        label: l.apelido ? `${l.nome} (@${l.apelido})` : l.nome,
        hint: `${l.count} apoiador(es)${l.cidade ? ' · ' + l.cidade : ''}`,
      })),
    [filterOpts.liderancas]
  );

  // Local map to render leader name in the table
  const leaderName = (id) => {
    const l = filterOpts.liderancas.find((x) => x.id === id);
    return l ? (l.apelido ? `${l.nome} (@${l.apelido})` : l.nome) : 'Sem liderança';
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...pendingFilters });
  };

  const clearFilters = () => {
    const cleared = {
      search: '',
      estado: '',
      cidade: '',
      lideranca_id: '',
      tipo_registro: 'identificado',
    };
    setPendingFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const handleDelete = async (supporter) => {
    const label = supporter.nome || supporter.whatsapp || 'este apoiador';
    if (!window.confirm(`Excluir ${label}? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/supporters/${supporter.id}`, { withCredentials: true });
      toast.success('Apoiador excluído');
      fetchRows();
      fetchInitial();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao excluir');
    }
  };

  // ===== Supporter edit dialog =====
  const [editingSupporter, setEditingSupporter] = useState(null);
  const [editForm, setEditForm] = useState({
    nome: '',
    whatsapp: '',
    email: '',
    estado: '',
    cidade: '',
    bairro: '',
    observacoes: '',
    status: 'ativo',
    lideranca_id: '',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Resolve which dobrada (if any) the supporter's leader belongs to and
  // surface the allowed states/cities. When the leader is bound to a dobrada
  // with `estado` set, the state dropdown collapses to that single state;
  // when `cidades[]` is also populated, the city dropdown is restricted too.
  // Falls back to the full list of Brazilian states and cities otherwise.
  const editScope = useMemo(() => {
    const leaderId = editForm.lideranca_id || editingSupporter?.lideranca_id;
    const leader = leaderId ? leadersFull.find((l) => l.id === leaderId) : null;
    const dobrada = leader?.dobrada_id
      ? dobradas.find((d) => d.id === leader.dobrada_id)
      : null;

    const restrictedEstado = (dobrada?.estado || '').toUpperCase();
    let availableStates;
    if (restrictedEstado) {
      const match = ESTADOS.find((e) => e.sigla === restrictedEstado);
      availableStates = match ? [match] : ESTADOS;
    } else {
      availableStates = ESTADOS;
    }

    // Ensure the supporter's CURRENT state stays visible even if outside
    // the dobrada restriction (admin needs to see it to migrate it).
    const currentEstado = (editForm.estado || '').toUpperCase();
    if (
      currentEstado &&
      !availableStates.some((e) => e.sigla === currentEstado)
    ) {
      const meta = ESTADOS.find((e) => e.sigla === currentEstado);
      if (meta) {
        availableStates = [meta, ...availableStates];
      } else {
        availableStates = [{ sigla: currentEstado, nome: currentEstado }, ...availableStates];
      }
    }

    // Cities: prefer the dobrada's curated list when the selected state matches
    let availableCities = [];
    const stateForCities = (editForm.estado || '').toUpperCase();
    if (stateForCities) {
      if (
        dobrada &&
        dobrada.estado &&
        dobrada.estado.toUpperCase() === stateForCities &&
        Array.isArray(dobrada.cidades) &&
        dobrada.cidades.length > 0
      ) {
        availableCities = [...dobrada.cidades];
      } else {
        availableCities = cidadesBrasil[stateForCities] || [];
      }
      // Include the supporter's CURRENT city as a fallback option if not in the list
      if (editForm.cidade && !availableCities.includes(editForm.cidade)) {
        availableCities = [editForm.cidade, ...availableCities];
      }
    }

    return { dobrada, availableStates, availableCities };
  }, [editForm.lideranca_id, editForm.estado, editForm.cidade, editingSupporter, leadersFull, dobradas]);

  const openEditSupporter = (s) => {
    setEditingSupporter(s);
    setEditForm({
      nome: s.nome || '',
      whatsapp: s.whatsapp || '',
      email: s.email || '',
      estado: s.estado || '',
      cidade: s.cidade || '',
      bairro: s.bairro || '',
      observacoes: s.observacoes || '',
      status: s.status || 'ativo',
      lideranca_id: s.lideranca_id || '',
    });
  };

  const handleEditSupporterSubmit = async (e) => {
    e.preventDefault();
    if (!editingSupporter) return;
    setEditSubmitting(true);
    try {
      const payload = { ...editForm };
      if (!payload.email) delete payload.email;
      if (!payload.observacoes) delete payload.observacoes;
      payload.lideranca_id = payload.lideranca_id || null;
      await axios.put(
        `${BACKEND_URL}/api/supporters/${editingSupporter.id}`,
        payload,
        { withCredentials: true }
      );
      toast.success('Apoiador atualizado');
      setEditingSupporter(null);
      fetchRows();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao atualizar apoiador');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Growth rate
  const growthRate = useMemo(() => {
    if (!evolution || !evolution.evolution || evolution.evolution.length < 14) return null;
    const half = Math.floor(evolution.evolution.length / 2);
    const first = evolution.evolution.slice(0, half).reduce((acc, d) => acc + d.total, 0);
    const second = evolution.evolution.slice(half).reduce((acc, d) => acc + d.total, 0);
    if (first === 0) return second > 0 ? 100 : 0;
    return Math.round(((second - first) / first) * 100);
  }, [evolution]);

  // Top cities from filter options (more accurate than current page)
  const cityDistribution = useMemo(() => {
    const counts = {};
    filterOpts.cidades.forEach((c) => {
      counts[c.cidade] = (counts[c.cidade] || 0) + c.count;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([cidade, total]) => ({ cidade, total }));
  }, [filterOpts.cidades]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const filtersActive =
    appliedFilters.search ||
    appliedFilters.estado ||
    appliedFilters.cidade ||
    appliedFilters.lideranca_id ||
    appliedFilters.tipo_registro !== 'identificado';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="apoiadores-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight">Apoiadores</h1>
            <p className="text-muted-foreground mt-2">
              Evolução e análise da base de apoiadores
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-import-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar planilha
            </Button>
            <ExportButton resource="supporters" />
            <Select
              value={String(periodDays)}
              onValueChange={(v) => setPeriodDays(parseInt(v))}
            >
              <SelectTrigger className="w-44" data-testid="select-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="14">Últimos 14 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="60">Últimos 60 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    Total
                  </p>
                  <p className="text-3xl font-bold mt-1">{stats.total}</p>
                </div>
                <Users className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    Identificados
                  </p>
                  <p className="text-3xl font-bold mt-1 text-primary">
                    {stats.identificados}
                  </p>
                </div>
                <UserCircle className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    Filtrados
                  </p>
                  <p className="text-3xl font-bold mt-1">
                    {pageTotal}
                  </p>
                </div>
                <FilterIcon className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    Crescimento
                  </p>
                  <p
                    className={`text-3xl font-bold mt-1 ${
                      growthRate > 0
                        ? 'text-green-600'
                        : growthRate < 0
                        ? 'text-red-600'
                        : ''
                    }`}
                  >
                    {growthRate !== null
                      ? `${growthRate > 0 ? '+' : ''}${growthRate}%`
                      : '—'}
                  </p>
                </div>
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Evolution Chart + Heatmap (side-by-side) */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle>Evolução da Base de Apoiadores</CardTitle>
              <CardDescription>
                Acumulado e novos apoiadores nos últimos {periodDays} dias
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              {evolution && evolution.evolution && evolution.evolution.length > 0 ? (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">
                      Acumulado (Total)
                    </h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={evolution.evolution}>
                        <defs>
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(224 85% 35%)" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="hsl(224 85% 35%)" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorIdent" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(142 71% 45%)" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="hsl(142 71% 45%)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={formatDate} />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(label) =>
                            new Date(label).toLocaleDateString('pt-BR')
                          }
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="acumulado_total"
                          stroke="hsl(224 85% 35%)"
                          fillOpacity={1}
                          fill="url(#colorTotal)"
                          strokeWidth={2}
                          name="Total acumulado"
                        />
                        <Area
                          type="monotone"
                          dataKey="acumulado_identificado"
                          stroke="hsl(142 71% 45%)"
                          fillOpacity={1}
                          fill="url(#colorIdent)"
                          strokeWidth={2}
                          name="Identificados acumulado"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">
                      Novos Apoiadores por Dia
                    </h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={evolution.evolution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={formatDate} />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(label) =>
                            new Date(label).toLocaleDateString('pt-BR')
                          }
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke="hsl(224 85% 35%)"
                          strokeWidth={2}
                          name="Total novos"
                        />
                        <Line
                          type="monotone"
                          dataKey="identificado"
                          stroke="hsl(142 71% 45%)"
                          strokeWidth={2}
                          name="Identificados"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <p className="text-center py-12 text-muted-foreground">
                  Sem dados de evolução para o período selecionado.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Heatmap (side-by-side with evolution) */}
          <Card className="flex flex-col">
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle>Mapa de apoiadores</CardTitle>
                  <CardDescription className="mt-1">
                    Distribuição geográfica em pins. Coordenadas arredondadas para preservar privacidade.
                  </CardDescription>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-3xl font-bold text-primary leading-none">{heatmap.total}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                    pontos
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              {heatmap.points.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-16 h-full flex items-center justify-center text-sm text-slate-500">
                  Sem apoiadores georreferenciados ainda.
                </div>
              ) : (
                <SupportersHeatmap points={heatmap.points} height={500} />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Cities */}
        {cityDistribution.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Top 5 Cidades por Apoiadores Identificados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {cityDistribution.map((c, idx) => {
                  const max = cityDistribution[0].total;
                  const pct = (c.total / max) * 100;
                  return (
                    <div key={c.cidade} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">
                          #{idx + 1} {c.cidade}
                        </span>
                        <span className="font-bold text-primary">{c.total}</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="font-bold text-sm">Filtros</span>
              <div className="ml-auto flex gap-2">
                {filtersActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Limpar
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={applyFilters}
                  disabled={rowsLoading}
                  data-testid="apply-filters-btn"
                >
                  <FilterIcon className="h-3.5 w-3.5 mr-1.5" /> Filtrar
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative lg:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, WhatsApp ou bairro..."
                  value={pendingFilters.search}
                  onChange={(e) => setPendingFilters({ ...pendingFilters, search: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyFilters();
                  }}
                  className="pl-9"
                  data-testid="search-apoiadores"
                />
              </div>
              <Combobox
                value={pendingFilters.estado}
                onChange={(v) =>
                  setPendingFilters({ ...pendingFilters, estado: v || '', cidade: '' })
                }
                options={estadoOptions}
                placeholder="Estado"
                searchPlaceholder="Buscar estado..."
                emptyLabel="Nenhum estado"
                testId="filter-estado"
              />
              <Combobox
                value={pendingFilters.cidade}
                onChange={(v) => setPendingFilters({ ...pendingFilters, cidade: v || '' })}
                options={cidadeOptions}
                placeholder="Cidade"
                searchPlaceholder="Buscar cidade..."
                emptyLabel="Sem cidade disponível"
                testId="filter-cidade"
              />
              <Combobox
                value={pendingFilters.lideranca_id}
                onChange={(v) =>
                  setPendingFilters({ ...pendingFilters, lideranca_id: v || '' })
                }
                options={liderancaOptions}
                placeholder="Liderança"
                searchPlaceholder="Buscar liderança..."
                emptyLabel="Nenhuma liderança"
                testId="filter-lideranca"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Tipo:</span>
              <Select
                value={pendingFilters.tipo_registro || 'all'}
                onValueChange={(v) =>
                  setPendingFilters({ ...pendingFilters, tipo_registro: v === 'all' ? '' : v })
                }
              >
                <SelectTrigger className="w-44 h-8 text-sm" data-testid="filter-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="identificado">Identificados</SelectItem>
                  <SelectItem value="anonimo">Anônimos</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground ml-auto">
                {pageTotal} registro{pageTotal === 1 ? '' : 's'} encontrado{pageTotal === 1 ? '' : 's'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Listing */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-muted-foreground">
                {appliedFilters.tipo_registro === 'anonimo'
                  ? 'Sessões anônimas sem identificação'
                  : appliedFilters.tipo_registro === 'identificado'
                  ? 'Apoiadores identificados'
                  : 'Todos os apoiadores'}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Por página:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPage(1);
                    setPageSize(parseInt(v));
                  }}
                >
                  <SelectTrigger className="w-20 h-8 text-sm" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {rowsLoading ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Carregando...</div>
            ) : rows.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                Nenhum apoiador encontrado.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-2 py-3 font-bold">Nome</th>
                      <th className="px-2 py-3 font-bold">WhatsApp</th>
                      <th className="px-2 py-3 font-bold">Localidade</th>
                      <th className="px-2 py-3 font-bold">Liderança</th>
                      <th className="px-2 py-3 font-bold text-right">Cadastro</th>
                      <th className="px-2 py-3 font-bold text-right w-12">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr
                        key={s.id}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                        data-testid={`apoiador-row-${s.id}`}
                      >
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <UserCircle className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium">{s.nome || (s.tipo_registro === 'anonimo' ? 'Sessão anônima' : '—')}</span>
                              {s.source === 'manual_upload' && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 self-start"
                                  data-testid="badge-manual-upload"
                                  title="Cadastro importado por planilha"
                                >
                                  <Upload className="h-2.5 w-2.5" />
                                  Cadastro manual
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-1 text-sm">
                            {s.whatsapp ? (
                              <>
                                <Phone className="h-3 w-3 text-muted-foreground" />
                                {s.whatsapp}
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-sm">
                          {s.estado && (
                            <span className="font-bold text-primary mr-1">{s.estado}</span>
                          )}
                          {s.cidade || '—'}
                          {s.bairro && (
                            <div className="text-xs text-muted-foreground">{s.bairro}</div>
                          )}
                        </td>
                        <td className="px-2 py-3 text-sm">{leaderName(s.lideranca_id)}</td>
                        <td className="px-2 py-3 text-right text-xs text-muted-foreground">
                          {new Date(s.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-2 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditSupporter(s)}
                            title="Editar apoiador"
                            data-testid={`edit-apoiador-${s.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(s)}
                            title="Excluir apoiador"
                            data-testid={`delete-apoiador-${s.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pageCount > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-muted-foreground">
                  Página {page} de {pageCount}
                </span>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => page > 1 && setPage(page - 1)}
                        className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        data-testid="page-prev"
                      />
                    </PaginationItem>
                    {Array.from({ length: pageCount }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === pageCount || Math.abs(p - page) <= 1)
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        return (
                          <React.Fragment key={p}>
                            {prev && p - prev > 1 && (
                              <PaginationItem>
                                <PaginationEllipsis />
                              </PaginationItem>
                            )}
                            <PaginationItem>
                              <PaginationLink
                                isActive={p === page}
                                onClick={() => setPage(p)}
                                className="cursor-pointer"
                              >
                                {p}
                              </PaginationLink>
                            </PaginationItem>
                          </React.Fragment>
                        );
                      })}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => page < pageCount && setPage(page + 1)}
                        className={page >= pageCount ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        data-testid="page-next"
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Edit supporter dialog */}
      <Dialog open={!!editingSupporter} onOpenChange={(v) => !v && setEditingSupporter(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-supporter-dialog">
          <DialogHeader>
            <DialogTitle>Editar apoiador</DialogTitle>
            <DialogDescription>
              Atualize os dados deste apoiador. Reatribuir a liderança move o crédito de captação.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSupporterSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sup-nome">Nome *</Label>
                <Input
                  id="sup-nome"
                  value={editForm.nome}
                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                  required
                  data-testid="edit-sup-nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sup-whatsapp">WhatsApp *</Label>
                <PhoneInput
                  id="sup-whatsapp"
                  value={editForm.whatsapp}
                  onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })}
                  required
                  data-testid="edit-sup-whatsapp"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="sup-email">E-mail</Label>
                <Input
                  id="sup-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="opcional"
                  data-testid="edit-sup-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sup-estado">Estado</Label>
                <Select
                  value={editForm.estado || 'none'}
                  onValueChange={(v) =>
                    setEditForm({
                      ...editForm,
                      estado: v === 'none' ? '' : v,
                      // When state changes, clear city to force a fresh pick
                      cidade: '',
                    })
                  }
                >
                  <SelectTrigger id="sup-estado" data-testid="edit-sup-estado">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sem estado —</SelectItem>
                    {editScope.availableStates.map((e) => (
                      <SelectItem key={e.sigla} value={e.sigla}>
                        {e.sigla} — {e.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editScope.dobrada?.estado && (
                  <p className="text-[10px] text-violet-600">
                    Restrito à dobrada {editScope.dobrada.nome} ({editScope.dobrada.estado})
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="sup-cidade">Cidade</Label>
                <Select
                  value={editForm.cidade || 'none'}
                  onValueChange={(v) => setEditForm({ ...editForm, cidade: v === 'none' ? '' : v })}
                  disabled={!editForm.estado}
                >
                  <SelectTrigger id="sup-cidade" data-testid="edit-sup-cidade">
                    <SelectValue placeholder={editForm.estado ? 'Selecione' : 'Escolha o estado primeiro'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Sem cidade —</SelectItem>
                    {editScope.availableCities.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editScope.dobrada?.cidades?.length > 0 &&
                  editForm.estado === editScope.dobrada.estado && (
                    <p className="text-[10px] text-violet-600">
                      Filtrado pelas cidades da dobrada ({editScope.dobrada.cidades.length})
                    </p>
                  )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="sup-bairro">Bairro</Label>
                <Input
                  id="sup-bairro"
                  value={editForm.bairro}
                  onChange={(e) => setEditForm({ ...editForm, bairro: e.target.value })}
                  data-testid="edit-sup-bairro"
                  placeholder="Texto livre"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sup-status">Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger id="sup-status" data-testid="edit-sup-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sup-lider">Liderança vinculada</Label>
                <Select
                  value={editForm.lideranca_id || 'none'}
                  onValueChange={(v) =>
                    setEditForm({ ...editForm, lideranca_id: v === 'none' ? '' : v })
                  }
                >
                  <SelectTrigger id="sup-lider" data-testid="edit-sup-lideranca">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem liderança</SelectItem>
                    {filterOpts.liderancas.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.apelido ? `${l.nome} (@${l.apelido})` : l.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sup-obs">Observações</Label>
              <Textarea
                id="sup-obs"
                value={editForm.observacoes}
                onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })}
                rows={3}
                data-testid="edit-sup-observacoes"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingSupporter(null)}
                data-testid="edit-sup-cancel"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={editSubmitting} data-testid="edit-sup-submit">
                {editSubmitting ? 'Salvando...' : 'Salvar alterações'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkImportWizard
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          // Refresh page data but keep wizard open on the result screen
          fetchInitial();
          fetchRows();
        }}
        primaryState={primaryState}
      />
    </Layout>
  );
};

